import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch } from '@store';
import {
  useAddTopUpVisibilityMutation,
  useLazyGetAllTopUpVisibilityQuery,
  useLazyGetCustomersTopUpQuery,
  useUpdateTopUpVisibilityMutation,
} from '../../../customers/store/services/customers';
import { setTopUpIds } from '../../store/slices/top-up-panel';

const useTopUpOptions = ({
  customerId,
  topUpOptions,
  topUpIds,
}: {
  customerId?: string;
  topUpOptions?: {
    companyName: string;
    id: string;
    visibleToClients: boolean;
  }[];
  topUpIds?: string[];
}) => {
  const [selectedTopUpOptionId, setSelectedTopUpOptionId] = useState('');

  const dispatch = useAppDispatch();

  const [
    getTopUp,
    { isLoading: topUpOptionsIsLoading, isFetching: topUpOptionsIsFetching },
  ] = useLazyGetCustomersTopUpQuery();
  const [
    getAllTopUpVisibility,
    {
      isLoading: topUpVisibilityIsLoading,
      isFetching: topUpVisibilityIsFetching,
    },
  ] = useLazyGetAllTopUpVisibilityQuery();

  const [
    addTopUpOptionVisibility,
    { isSuccess: addTopUpOptionVisibilityIsSuccess },
  ] = useAddTopUpVisibilityMutation();
  const [
    updateTopUpOptionVisibility,
    { isSuccess: updateTopUpOptionVisibilityIsSuccess },
  ] = useUpdateTopUpVisibilityMutation();

  const getNextTopUpOptions = (cursor: string) => {
    getTopUp({ cursor });
  };

  const getAllTopUpVisibilityAction = useCallback(
    async (query: string) => {
      await getAllTopUpVisibility({
        organizationId: customerId as string,
        query,
      });
    },
    [customerId],
  );

  const addVisibilityAction = async (visible: boolean, id: string) => {
    setSelectedTopUpOptionId(id);

    await addTopUpOptionVisibility({
      id,
      body: {
        organizationId: customerId as string,
        visible,
      },
    });
  };

  const updateVisibiliyAction = async (
    visible: boolean,
    id: string,
    pspId: string,
    pspPartnerId: string,
  ) => {
    setSelectedTopUpOptionId(id);

    await updateTopUpOptionVisibility({
      id,
      pspId,
      body: {
        pspPartnerId,
        organizationId: customerId as string,
        visible,
      },
    });
  };

  useEffect(() => {
    if (customerId && topUpOptions) {
      const nextTopUpIds = ([] as string[]).concat(topUpIds as string[]);

      const topUpIdsValues = topUpOptions?.map((item) => item.id);

      topUpIdsValues?.forEach((item) => {
        if (!nextTopUpIds?.find((id) => item === id)) {
          nextTopUpIds.push(item);
        }
      });

      if (
        topUpIdsValues &&
        topUpIdsValues?.filter((item) => topUpIds?.every((id) => id !== item))
          ?.length
      ) {
        const query = `&pspPartnerId=${topUpIdsValues
          ?.filter((item) => topUpIds?.every((id) => id !== item))
          ?.join('&pspPartnerId=')}`;

        getAllTopUpVisibilityAction(query);
        dispatch(setTopUpIds(nextTopUpIds));
      }
    }
  }, [customerId, topUpOptions, topUpIds]);

  return {
    selectedTopUpOptionId,
    setSelectedTopUpOptionId,
    addTopUpOptionVisibilityIsSuccess,
    updateTopUpOptionVisibilityIsSuccess,
    addVisibilityAction,
    updateVisibiliyAction,
    getNextTopUpOptions,
    topUpOptionsIsLoading,
    topUpOptionsIsFetching,
    topUpVisibilityIsLoading,
    topUpVisibilityIsFetching,
  };
};

export default useTopUpOptions;
